/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

import {IpAddress} from "../../../util/ip-valid";
import {AdServerConfig} from "../../api";
import {Injectable} from '@angular/core';
import {AdnLogService} from '../../../util/adn-log.service';
import {AdnHttpService} from '../../../util/adn-http.service';
import {AdnHttpSender, createAdnHttpResourceSender} from '../../../util/create-adn-http-resource';
import {AuthHttpInterceptorService} from '../../../auth/auth-http-interceptor.service';
import {ApiErrorHandlerService} from '../../../../app/common/directives/error/api-error-handler.service';



@Injectable()
export class DiagRequestResource {
  private readonly send: AdnHttpSender;

  constructor(
    private readonly adnLog: AdnLogService,
    AdnHttp: AdnHttpService,
    authHttpInterceptor: AuthHttpInterceptorService,
    ApiErrorHandler: ApiErrorHandlerService
  ) {
    this.send = createAdnHttpResourceSender(AdnHttp, authHttpInterceptor, ApiErrorHandler);
  }

  private parseDiagnosticResponse(data: any) {
    if (!_.isString(data)) {
      return data;
    }

    const sanitised = data.split('').filter((character: string) => {
      const charCode = character.charCodeAt(0);
      return !(charCode < 32 && charCode !== 9 && charCode !== 10 && charCode !== 13);
    }).join('');
    return JSON.parse(sanitised);
  }

  private updateParams(val: any) {
    if (val) {
      return _.map(_.filter(val.split(","), (c: string) => {
        return _.trim(c);
      }), (c: string) => {
        return _.trim(c);
      });
    }
    return val;
  }

  diagReq(type, qStringParams) {
    if (!qStringParams.auId) {
      this.adnLog.warn("Need an auId to do the diagnostic request.");
    }
    const qs = _.clone(qStringParams);

    qs.segments = this.updateParams(qs.segments);
    qs.c = this.updateParams(qs.c);
    qs.keywords = this.updateParams(qs.keywords);

    if (_.isArray(qs.kv)) {
      qs.kv = _.map(_.filter(qs.kv, (kv) => {
        return _.trim(kv.k) && _.trim(kv.v);
      }), (kv) => {
        const entry = {};
        entry[_.trim(kv.k)] = this.updateParams(kv.v);
        return entry;
      });
    }

    if (qs.requestType === 'NATIVE') {
      qs.tt = 'native';
    } else if (qs.requestType === 'VAST') {
      qs.tt = 'vast';
    }

    if (qs.ip) {
      if (!IpAddress.isValid(qs.ip)) {
        return Promise.reject({info: 'Must enter a valid IP address to test against'});
      }
    }
    if (qs.longitude || qs.latitude) {
      if (!(qs.longitude && qs.latitude)) {
        return Promise.reject({info: 'Both latitude and longitude must be specified. Not just one or the other.'});
      }
      if (_.get(qs, 'longitude').length > 0 && isNaN(qs.longitude)) {
        return Promise.reject({info: 'If specified, both latitude and longitude must be a number.'});
      }
      if (_.get(qs, 'latitude').length > 0 && isNaN(qs.latitude)) {
        return Promise.reject({info: 'If specified, both latitude and longitude must be a number.'});
      }
      qs.latitude = parseFloat(qs.latitude);
      qs.longitude = parseFloat(qs.longitude);
    }
    const uri = AdServerConfig.obtain().getUri('i');
    return this.send({
      method: 'GET',
      url: uri,
      params: _.assign({}, {format: type, tzo: new Date().getTimezoneOffset(), gdpr: 0}, qs),
      responseType: 'text'
    }).then((data) => this.parseDiagnosticResponse(data));
  }

  dx(qStringParams) {
    return this.diagReq("dx", qStringParams);
  }

  dxMx(qStringParams) {
    return this.diagReq("dxMx", qStringParams);
  }
}
